export const howItWorksPages = [
  {
    slug: 'home-blood-collection',
    title: 'How Home Blood Collection Works Guide | Homvisit Kerala',
    h1: 'How Home Blood Collection Works',
    description: 'Step-by-step: how Homvisit runs home blood collection in Kerala — scheduling, phlebotomy visit, labelling, and sample handoff. Homvisit serves labs…',
    sections: [
      {
        heading: 'Before the visit',
        paragraphs: [
          'The partner or patient shares location, preferred slot, and test requirements. Homvisit assigns a phlebotomist who can reach the area within the agreed window.',
          'Patients should keep ID ready and follow any fasting instructions from the ordering clinician or lab.',
        ],
      },
      {
        heading: 'During the visit',
        paragraphs: [
          'The phlebotomist verifies identity, explains the draw, and uses sterile single-use consumables.',
          'Samples are labelled immediately with the identifiers your lab or partner requires.',
        ],
      },
      {
        heading: 'After collection',
        paragraphs: [
          'Samples move into the agreed logistics path — partner courier, drop point, or coordinated pickup.',
          'Any exceptions (difficult draw, patient unavailable) are logged so ops can reschedule without silent failures.',
        ],
      },
    ],
    steps: [
      'Request / partner order created',
      'Phlebotomist assigned by locality',
      'Home visit + ID check + draw',
      'Label + pack + handoff',
      'Lab processing (by your lab partner)',
    ],
    faqs: [
      {
        q: 'How long does a typical home visit take?',
        a: 'Most straightforward blood draws complete in 10–20 minutes on site, excluding travel.',
      },
    ],
    related: ['/services/home-blood-collection', '/how-it-works/sample-transport'],
  },
  {
    slug: 'corporate-medical-camps',
    title: 'How Corporate Medical Camps Work | Homvisit Kerala',
    h1: 'How Corporate Medical Camps Work',
    description: 'How Homvisit plans and executes corporate medical camps in Kerala — briefing, staffing, queue flow, and close-out. Homvisit serves labs, hospitals, and HR…',
    sections: [
      {
        heading: 'Planning',
        paragraphs: [
          'HR shares headcount, shifts, venue layout, and whether fasting draws are required. We recommend station count based on throughput goals.',
        ],
      },
      {
        heading: 'Camp floor flow',
        paragraphs: [
          'Typical flow: registration → vitals (if partner provides) → phlebotomy → exit. Homvisit owns the phlebotomy stations and labelling discipline.',
        ],
      },
      {
        heading: 'Close-out',
        paragraphs: [
          'We reconcile expected vs collected samples, note absentees, and confirm handoff to the diagnostic partner.',
        ],
      },
    ],
    steps: [
      'Corporate brief + date lock',
      'Staffing & consumables plan',
      'Camp setup & briefing',
      'Live collection',
      'Reconciliation & dispatch',
    ],
    faqs: [
      {
        q: 'Can camps run for night-shift employees?',
        a: 'Yes — early-morning or evening windows are common for factories and hospitals.',
      },
    ],
    related: [
      '/services/corporate-health-camps',
      '/resources/health-camp-checklist',
      '/insights/corporate-health-camps-planning-checklist',
    ],
  },
  {
    slug: 'sample-transport',
    title: 'How Sample Transport Works | Homvisit Kerala Guide',
    h1: 'How Sample Transport Works',
    description: 'Overview of sample movement after collection — packaging, timelines, and handoff responsibilities in Kerala operations. Homvisit serves labs, hospitals, and…',
    sections: [
      {
        heading: 'Packaging',
        paragraphs: [
          'Tubes are capped, labelled, and secondary-contained to prevent leakage. Biohazard norms are followed for sharps and waste separately from samples.',
        ],
      },
      {
        heading: 'Movement',
        paragraphs: [
          'Depending on the partner model, Homvisit hands samples to the lab’s logistics, a designated drop, or a coordinated pickup. Time-sensitive analytes need faster paths.',
        ],
      },
      {
        heading: 'Accountability',
        paragraphs: [
          'Counts and exception notes travel with the batch so the receiving lab can match intake to field reality.',
        ],
      },
    ],
    steps: [
      'Label verification',
      'Secondary containment',
      'Batch list',
      'Handoff / pickup',
      'Lab intake confirmation',
    ],
    faqs: [
      {
        q: 'Do you run a statewide courier company?',
        a: 'We coordinate collection-side movement and partner logistics. Long-haul courier networks may be provided by the diagnostic partner.',
      },
    ],
    related: ['/how-it-works/cold-chain-management', '/insights/cold-chain-integrity-sample-transport'],
  },
  {
    slug: 'quality-assurance',
    title: 'Quality Assurance Process | Homvisit Kerala Camp Guide',
    h1: 'Quality Assurance Process',
    description: 'How Homvisit approaches quality in phlebotomy and sample collection — identity, labelling, hygiene, and feedback loops. Homvisit serves labs, hospitals, and…',
    sections: [
      {
        heading: 'Quality pillars',
        paragraphs: [
          'Correct patient identification, sterile technique, accurate labelling, and timely handoff are non-negotiable.',
          'We treat redraws and labelling errors as process signals — not just individual mistakes.',
        ],
      },
      {
        heading: 'Feedback loop',
        paragraphs: [
          'Partner labs can share rejection reasons. We coach the network and adjust briefings before the next camp wave.',
        ],
      },
    ],
    steps: [
      'SOP briefing',
      'On-site checks',
      'Exception logging',
      'Partner reject review',
      'Retraining if needed',
    ],
    faqs: [],
    related: ['/resources/sample-collection-sop', '/resources/nabl-guidelines'],
  },
  {
    slug: 'cold-chain-management',
    title: 'Cold Chain Management Process Guide | Homvisit Kerala',
    h1: 'Cold Chain Management',
    description: 'Cold chain basics for sample integrity — when temperature control matters and how Homvisit plans camp and field collections. Homvisit serves labs, hospitals…',
    sections: [
      {
        heading: 'When cold chain matters',
        paragraphs: [
          'Not every tube needs ice, but many analytes and summer Kerala temperatures make temperature discipline important. Requirements come from the ordering lab’s test menu.',
        ],
      },
      {
        heading: 'Field practice',
        paragraphs: [
          'For temperature-sensitive work, we plan ice packs / coolers, minimise open-time, and avoid long unscheduled holds between draw and handoff.',
        ],
      },
    ],
    steps: [
      'Lab defines temperature needs',
      'Kit cold-pack preparation',
      'Minimise hold time',
      'Document delays',
      'Handoff to controlled logistics',
    ],
    faqs: [
      {
        q: 'Do you guarantee refrigerated trucks statewide?',
        a: 'Cold-chain logistics capability depends on the partner model. We design collection timing to protect integrity within agreed constraints.',
      },
    ],
    related: ['/how-it-works/sample-transport', '/insights/cold-chain-integrity-sample-transport'],
  },
  {
    slug: 'digital-tracking',
    title: 'Digital Tracking System | Homvisit Kerala Camp Guide',
    h1: 'Digital Tracking System',
    description: 'How Homvisit tracks collection requests, assignments, and exceptions — practical digital coordination for Kerala field ops. Homvisit serves labs, hospitals…',
    sections: [
      {
        heading: 'What we track',
        paragraphs: [
          'Request → assignment → completion / exception. For camps: expected headcount vs collected tubes.',
          'Partners can receive operational summaries rather than opaque “done” messages.',
        ],
      },
      {
        heading: 'What we are not',
        paragraphs: [
          'We are not replacing your LIS. Digital tracking here means operational visibility for field collection, integrated with your systems as agreed.',
        ],
      },
    ],
    steps: [
      'Capture request',
      'Assign collector',
      'Status update',
      'Exception flag',
      'Daily / camp summary',
    ],
    faqs: [],
    related: ['/services/diagnostic-lab-partner'],
  },
  {
    slug: 'phlebotomist-training-standards',
    title: 'Phlebotomist Training Standards Guide | Homvisit Kerala',
    h1: 'Phlebotomist Training Standards',
    description: 'Training expectations for Homvisit network phlebotomists — certification, hygiene, ID protocols, and camp readiness. Homvisit serves labs, hospitals, and HR…',
    sections: [
      {
        heading: 'Baseline',
        paragraphs: [
          'We prefer DMLT / MLT / phlebotomy certificate holders with practical draw experience. Freshers may join with supervised camp exposure.',
        ],
      },
      {
        heading: 'Homvisit briefing',
        paragraphs: [
          'Before deployment: partner SOP, labelling rules, professional conduct, and escalation contacts.',
        ],
      },
    ],
    steps: [
      'Credential check',
      'SOP briefing',
      'Shadow / supervised start (as needed)',
      'Solo assignment',
      'Ongoing feedback',
    ],
    faqs: [
      {
        q: 'How do phlebotomists join?',
        a: 'Through our always-open Join Network form on the jobs pages.',
      },
    ],
    related: ['/jobs', '/services/healthcare-staffing'],
  },
]

export function getHowItWorksPage(slug) {
  return howItWorksPages.find((p) => p.slug === slug) || null
}
